import {IconName} from '@wandb/weave/components/Icon';
import {
  ToggleButtonGroup,
  ToggleOption,
} from '@wandb/weave/components/ToggleButtonGroup';
import React, {useState} from 'react';

export const DEFAULT_ROW_HEIGHT = 32;
export const LINE_HEIGHT = 24;
export const DEFAULT_COLUMN_WIDTH = 140;

// Make sure that these match the values in the run_comparer.spec.ts file
enum CellRowHeight {
  Small = 'Small row height',
  Medium = 'Medium row height',
  Large = 'Large row height',
  ExtraLarge = 'Extra large row height',
}

export const ROW_HEIGHTS_IN_ORDER = [
  CellRowHeight.Small,
  CellRowHeight.Medium,
  CellRowHeight.Large,
  CellRowHeight.ExtraLarge,
];

const ROW_OPTION_ICONS: Record<CellRowHeight, IconName> = {
  [CellRowHeight.Small]: 'row-height-small',
  [CellRowHeight.Medium]: 'row-height-medium',
  [CellRowHeight.Large]: 'row-height-large',
  [CellRowHeight.ExtraLarge]: 'row-height-xlarge',
};

const ROW_OPTIONS: ToggleOption[] = Object.values(CellRowHeight).map(
  option => ({
    label: option,
    value: option,
    icon: ROW_OPTION_ICONS[option],
    iconOnly: true,
    tooltip: option,
  })
);

const NUM_LINES_PER_ROW: Record<CellRowHeight, number> = {
  [CellRowHeight.Small]: 1,
  [CellRowHeight.Medium]: 3,
  [CellRowHeight.Large]: 9,
  [CellRowHeight.ExtraLarge]: 27,
};

enum CellColumnWidth {
  Small = 'Small column width',
  Medium = 'Medium column width',
  Large = 'Large column width',
}

const COLUMN_OPTION_ICONS: Record<CellColumnWidth, IconName> = {
  [CellColumnWidth.Small]: 'content-narrow',
  [CellColumnWidth.Medium]: 'content-wide',
  [CellColumnWidth.Large]: 'content-full-width',
};

const COLUMN_OPTIONS: ToggleOption[] = Object.values(CellColumnWidth).map(
  option => ({
    label: option,
    value: option,
    icon: COLUMN_OPTION_ICONS[option],
    iconOnly: true,
    tooltip: option,
  })
);

const COLUMN_WIDTHS_IN_ORDER = [
  CellColumnWidth.Small,
  CellColumnWidth.Medium,
  CellColumnWidth.Large,
];

// utils to convert between the button's enum and the spec's pixel values
function getRowHeightPixels(rowHeight: CellRowHeight): number {
  return (NUM_LINES_PER_ROW[rowHeight] - 1) * LINE_HEIGHT + DEFAULT_ROW_HEIGHT;
}
function getColumnWidthPixels(columnWidth: CellColumnWidth): number {
  return (
    (COLUMN_WIDTHS_IN_ORDER.indexOf(columnWidth) + 1) * DEFAULT_COLUMN_WIDTH
  );
}

function getPixelRowHeight(pixels: number): CellRowHeight {
  return ROW_HEIGHTS_IN_ORDER.find(
    rowHeight => getRowHeightPixels(rowHeight) >= pixels
  )!;
}
export function getNumLinesFromPixelRowHeight(pixels: number): number {
  return NUM_LINES_PER_ROW[getPixelRowHeight(pixels)];
}
function getPixelColumnWidth(pixels: number): CellColumnWidth {
  return COLUMN_WIDTHS_IN_ORDER.find(
    columnWidth => getColumnWidthPixels(columnWidth) >= pixels
  )!;
}

export const CellDimensionsSelector = ({
  rowHeight,
  columnWidth,
  setRowHeight,
  setColumnWidth,
}: {
  rowHeight: number;
  columnWidth: number;
  setRowHeight: (rowHeight: number) => void;
  setColumnWidth: (columnWidth: number) => void;
}) => {
  const [localColumnWidth, setLocalColumnWidth] = useState(columnWidth);
  const [localRowHeight, setLocalRowHeight] = useState(rowHeight);

  return (
    <div className="mr-[15px] flex flex-wrap items-center gap-8">
      <ToggleButtonGroup
        options={COLUMN_OPTIONS}
        value={getPixelColumnWidth(localColumnWidth)}
        size="medium"
        isDisabled={localColumnWidth !== columnWidth} // Disable the column width button if the local column width is not the same as the prop column width since that means we're still updating
        onValueChange={value => {
          if (
            Object.values(CellColumnWidth).includes(value as CellColumnWidth) // when clicking a button that's already selected, the value is empty string. We don't want to allow de-selecting, so only update if it's a real value.
          ) {
            setLocalColumnWidth(getColumnWidthPixels(value as CellColumnWidth));
            setColumnWidth(getColumnWidthPixels(value as CellColumnWidth));
          }
        }}
      />
      <ToggleButtonGroup
        options={ROW_OPTIONS}
        value={getPixelRowHeight(localRowHeight)}
        isDisabled={localRowHeight !== rowHeight} // Disable the column width button if the local column width is not the same as the prop column width since that means we're still updating
        size="medium"
        onValueChange={value => {
          if (
            Object.values(CellRowHeight).includes(value as CellRowHeight) // when clicking a button that's already selected, the value is empty string. We don't want to allow de-selecting, so only update if it's a real value.
          ) {
            setLocalRowHeight(getRowHeightPixels(value as CellRowHeight));
            setRowHeight(getRowHeightPixels(value as CellRowHeight));
          }
        }}
      />
    </div>
  );
};
